import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as publicApi from '../api/publicClient';

const SiteContext = createContext(null);

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [digitalInvitations, setDigitalInvitations] = useState([]);
  const [featuredDigitalInvitation, setFeaturedDigitalInvitation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [siteSettings, invitations] = await Promise.all([
          publicApi.getSettings(),
          publicApi.getDigitalInvitations(),
        ]);
        if (cancelled) return;

        setSettings(siteSettings);
        setDigitalInvitations(invitations);

        if (invitations.length > 0) {
          try {
            const featured = await publicApi.getDigitalInvitation(invitations[0].slug);
            if (!cancelled) setFeaturedDigitalInvitation(featured);
          } catch {
            if (!cancelled) setFeaturedDigitalInvitation(null);
          }
        } else if (!cancelled) {
          setFeaturedDigitalInvitation(null);
        }
      } catch {
        if (!cancelled) {
          setSettings(null);
          setDigitalInvitations([]);
          setFeaturedDigitalInvitation(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const siteName = settings?.siteName ?? publicApi.staticSite.siteName;

  const getWhatsAppUrl = useCallback((message) => {
    const number = settings?.whatsappNumber;
    const defaultMessage = settings?.defaultWhatsappMessage;
    if (number) {
      const params = new URLSearchParams();
      const text = message ?? defaultMessage ?? '';
      if (text) params.set('text', text);
      const q = params.toString();
      return `https://wa.me/${number}${q ? `?${q}` : ''}`;
    }
    return publicApi.staticSite.getWhatsAppUrl(message);
  }, [settings]);

  const featuredDigitalSlug = digitalInvitations[0]?.slug ?? 'elifhakan';

  const value = useMemo(
    () => ({
      loading,
      siteName,
      settings,
      digitalInvitations,
      featuredDigitalSlug,
      featuredDigitalInvitation,
      getWhatsAppUrl,
    }),
    [
      loading,
      siteName,
      settings,
      digitalInvitations,
      featuredDigitalSlug,
      featuredDigitalInvitation,
      getWhatsAppUrl,
    ],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used within SiteProvider');
  return ctx;
}
